import { useOneClickTradingV2 } from '@ui/features/OneClickTrading/hooks/useOneClickTradingV2';
import { useInvalidateLimitOrderQuery } from '@ui/features/Trade/hooks/useInvalidateQuery';
import { useDataChainId } from '@ui/hooks/datachain';
import { Order } from '@ui/hooks/trade/useTradeOrders/types';
import { useMutationToast } from '@ui/hooks/useMutationToast';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { pollV2IntentStatus } from '@ui/services/backend/oneClickV2Service';
import LimitOrderFacet from '@ui/services/contract/abis/LimitOrderFacet';
import { getContractAddress } from '@ui/services/contract/constants';
import { TxnMutateCallBackParams } from '@ui/types/tx';
import { extractErrorMessage } from '@ui/utils/extractContractError';
import { isNil } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';

import { useCancelLimitOrderToastOpts } from './useCancelLimitOrderToastOpts';

export const useCancelLimitOrder = ({
  order,
  onSuccess,
  onError,
  onSettled,
}: {
  order: Order;
} & TxnMutateCallBackParams) => {
  const chainId = useDataChainId();
  const mutationToastInfo = useMutationToast();
  const { onErrorToastOpts, onMutateToastOpts, onSuccessToastOpts } =
    useCancelLimitOrderToastOpts();

  const invalidateLimitOrderQuery = useInvalidateLimitOrderQuery();

  const orderHash = order.id;

  const {
    isReady: canUseOneClickTradingV2,
    isSubmittingLimitCancel: isSubmittingLimitCancelV2,
    sendLimitCancel: sendLimitCancelV2,
  } = useOneClickTradingV2();
  const [isAwaitingOneClickResult, setIsAwaitingOneClickResult] = useState(false);

  const { data, writeContract, isPending } = useTxnWriteContract({
    onSuccessToastOpts,
    onMutateToastOpts,
    onErrorToastOpts,
    onSuccess: () => {
      invalidateLimitOrderQuery();
      onSuccess?.();
    },
    onError,
    onSettled,
  });

  const handleOneClickCancelV2 = useCallback(async () => {
    if (!canUseOneClickTradingV2) return;

    setIsAwaitingOneClickResult(true);
    mutationToastInfo.onMutate({
      title: 'In Progress',
      description: '⚡️ One-click V2 cancel limit order in progress',
    });

    try {
      const intentHash = await sendLimitCancelV2(orderHash as `0x${string}`);

      const status = await pollV2IntentStatus(intentHash);
      if (!status.success) {
        throw new Error(
          status.reason || 'One-click V2 cancel limit order execution failed',
        );
      }

      invalidateLimitOrderQuery();

      mutationToastInfo.onSuccess({
        title: '⚡️ Completed',
        description: 'Limit order cancelled via one-click V2',
        txHash: status.txnHash as `0x${string}`,
      });

      onSuccess?.();
      return status.txnHash;
    } catch (error) {
      mutationToastInfo.onError({
        errorMsg:
          error instanceof Error
            ? extractErrorMessage(error.message) ?? error.message
            : undefined,
        onErrorToastOpts: {
          description: 'One-click V2 cancel limit order failed, please try again',
        },
      });
      throw error;
    } finally {
      mutationToastInfo.onSettled();
      setIsAwaitingOneClickResult(false);
    }
  }, [
    canUseOneClickTradingV2,
    invalidateLimitOrderQuery,
    mutationToastInfo,
    onSuccess,
    orderHash,
    sendLimitCancelV2,
  ]);

  const write = useCallback(() => {
    // OneClick V2 path (priority)
    if (canUseOneClickTradingV2) {
      return handleOneClickCancelV2();
    }

    const leverUpAddress = getContractAddress('LeverUp', chainId);
    if (isNil(leverUpAddress)) return;

    return writeContract({
      abi: LimitOrderFacet,
      address: leverUpAddress,
      functionName: 'cancelLimitOrder',
      args: [orderHash],
    });
  }, [
    chainId,
    orderHash,
    writeContract,
    canUseOneClickTradingV2,
    handleOneClickCancelV2,
  ]);

  const isPendingFinal =
    isPending || isSubmittingLimitCancelV2 || isAwaitingOneClickResult;

  return useMemo(() => {
    return {
      data,
      writeContract: write,
      isPending: isPendingFinal,
    };
  }, [data, isPendingFinal, write]);
};
